import { useCallback, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import { MdDelete } from 'react-icons/md'

import type { ReactElement } from 'react'
import type { FileWithPath } from 'react-dropzone'

import Button from '~/components/Buttons/Button'
import Modal from '~/components/Modal'
import { ModalBody, ModalFooter, ModalHeader } from '~/components/Modal/Layout'
import MwMLoader from '~/components/MwMLoader'
import { useCreateFileMutation } from '~/queries/files'
import { useCreateUserFileMutation, useUploadUserFileMutation } from '~/queries/user-file'
import { formatBytes } from '~/utils/sizes'
import { displayToast } from '~/utils/toast'

interface Props {
  directoryId: string
  isOpen: boolean
  onCloseModal: VoidFunction
  refetch: VoidFunction
}

const UploadFileModal = ({ directoryId, isOpen, onCloseModal, refetch }: Props): ReactElement => {
  /** Local state */

  const [loading, setLoading] = useState(false)

  const [files, setFiles] = useState<FileWithPath[]>()

  /** Mutation */

  const { mutateAsync: createFileMutation } = useCreateFileMutation()

  const { mutateAsync: createUserFileMutation } = useCreateUserFileMutation()

  const { mutateAsync: uploadUserFileMutation } = useUploadUserFileMutation()

  /** Handlers */

  const handleCloseModal = () => {
    setFiles(undefined)
    onCloseModal()
  }

  const handleRemoveFileFromUpload = (idx: number) => () => {
    const tempArray = [...(files || [])]
    tempArray.splice(idx, 1)
    setFiles(tempArray)
  }

  const submitFile = async (file: FileWithPath) => {
    try {
      const { id, upload_url } = await createFileMutation({
        mimeType: file.type || 'application/octet-stream',
        name: file.name,
        size: file.size,
      })

      await uploadUserFileMutation({ download_url: upload_url, file })
      await createUserFileMutation({ directory_id: directoryId, file_id: id })
    } catch (e) {
      throw e
    }
  }

  const handleBatchSubmit = async () => {
    if (files) {
      setLoading(true)
      try {
        const promises = files.map((file) => submitFile(file))
        await Promise.all(promises)
        refetch()
        displayToast(`${files.length} File${files.length > 1 ? 's' : ''} uploaded`, 'success')
        setFiles(undefined)
        onCloseModal()
      } catch {
        displayToast('A file could not be uploaded!', 'error')
      } finally {
        setLoading(false)
      }
    }
  }

  const handleDrop = useCallback((files: FileWithPath[]) => {
    files.forEach((file: FileWithPath) => {
      const reader = new FileReader()

      reader.onabort = () => displayToast('file reading was aborted', 'error')
      reader.onerror = () => displayToast('file reading has failed', 'error')
      reader.onload = () => {}
      reader.readAsArrayBuffer(file)
    })
    setFiles(files)
  }, [])

  const { getInputProps, getRootProps } = useDropzone({ onDrop: handleDrop })

  return (
    <Modal className="upload-file-modal" isOpen={isOpen} onCloseModal={onCloseModal}>
      <ModalHeader>Add files</ModalHeader>
      <ModalBody>
        {loading ? (
          <div className="loader-container">
            <MwMLoader category="item" />
          </div>
        ) : files ? (
          files.map(({ name, size }, idx) => (
            <div className="file-list__container" key={idx}>
              <div className="file-list__container__infos">
                <div className="file-list__container__infos__name">{name}</div>
                <div className="file-list__container__infos__size">{formatBytes(size, 2)}</div>
              </div>
              <MdDelete
                className="file-list__container__remove-icon"
                onClick={handleRemoveFileFromUpload(idx)}
                size={13}
              />
            </div>
          ))
        ) : (
          <div className="dropzone__container" {...getRootProps()}>
            <input {...getInputProps()} />
            <p>
              Drag & drop your file here, or <em>click</em> to select a file.
              <br /> Please drag one file at a time.
            </p>
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <Button ghost onClick={handleCloseModal}>
          Cancel
        </Button>
        <Button disabled={!files || loading} onClick={handleBatchSubmit}>
          Upload
        </Button>
      </ModalFooter>
    </Modal>
  )
}

export default UploadFileModal
